import React, { useMemo, useState, useCallback, memo, useEffect } from 'react';
import { isEqual } from 'lodash';
import { roles } from 'helpers/getRoles';

// material-ui
import {
  Box,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
  Hidden,
  Tooltip,
} from '@material-ui/core';
import { Delete } from '@material-ui/icons';

// components
import IconButton from 'components/IconButton';
import Autocomplete from 'components/Autocomplete';

// selectors
import getLoadingFromState from 'store/selectors/getLoadingFromState';
import getMembers from 'store/selectors/getMembers';
import getCurrentGroupId from 'store/selectors/getCurrentGroupId';
import getIsCurrentGroupCouncil from 'store/selectors/getIsCurrentGroupCouncil';

// actions
import { updatePhoneNumber as updatePhoneNumberAction } from 'store/actions/phoneNumberActions';
import { fetchMembersForGroup as fetchMembersForGroupAction } from 'store/actions/memberActions';

// hooks
import { useDispatch, useSelector } from 'react-redux';

// helpers
import formatDisplayedPhoneNumber from 'helpers/formatDisplayedPhoneNumber';

type Props = {
  id: number;
  twilioNumber: string;
  account: number;
  allowCallForwarding: boolean;
  twoWayMessagingEnabled: boolean;
  onDelete: (id: number) => void;
};

const PhoneNumberItem = ({
  allowCallForwarding: initialAllowCallForwarding,
  twoWayMessagingEnabled: initialTwoWayMessagingEnabled,
  twilioNumber,
  id,
  onDelete,
  account,
}: Props) => {
  const dispatch = useDispatch();
  const [currentAccount, setCurrentAccount] = useState();

  const [allowCallForwarding, setAllowCallForwarding] = useState(
    initialAllowCallForwarding
  );
  const [disableForwarding, setDisableForwarding] = useState(false);
  const [twoWayMessagingEnabled, setTwoWayMessagingEnabled] = useState(
    initialTwoWayMessagingEnabled
  );

  const displayedPhoneNumber = formatDisplayedPhoneNumber(twilioNumber);

  const { fetchMembersForGroup, updatePhoneNumber } = useMemo(
    () => ({
      fetchMembersForGroup: (payload: any) =>
        dispatch(fetchMembersForGroupAction(payload)),
      updatePhoneNumber: (payload: any) =>
        dispatch(updatePhoneNumberAction(payload)),
    }),
    [dispatch]
  );

  const {
    currentGroupId,
    deleteLoading,
    memberLoading,
    members,
    isCurrentGroupCouncil,
  } = useSelector(
    state => ({
      deleteLoading: getLoadingFromState(
        'phoneNumber',
        `delete-${id}`,
        false
      )(state),
      memberLoading: getLoadingFromState('member', `fetch-${id}`, false)(state),
      members: getMembers()(state).toJS() as any,
      currentGroupId: getCurrentGroupId(state),
      isCurrentGroupCouncil: getIsCurrentGroupCouncil(state),
    }),
    isEqual
  );

  useEffect(() => {
    if (!(currentAccount as any)?.value && members.length && id) {
      const initialMember =
        members.find(
          (member = {}) =>
            (member as any).account?.id?.toString() === account?.toString(),
          {},
          {}
        ) || {};
      setCurrentAccount({
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ label: string; value: any; }' ... Remove this comment to see the full error message
        label: `${initialMember?.account?.firstName ||
          'Unassigned'} ${initialMember?.account?.lastName || ''}`,
        value: initialMember?.account?.id,
      });
      setDisableForwarding(!initialMember?.account?.phoneNumber);
    }
  }, [members.length]); // eslint-disable-line

  // Fetches the currently assigned account if it isn't in the first 10 leads paginated
  useEffect(() => {
    if ((currentAccount as any)?.value && account && members.length) {
      const accountIds = members.map((member = {}) =>
        (member as any).account?.id?.toString()
      );
      const isAccountInSuggestions = accountIds.includes(account?.toString());
      if (!isAccountInSuggestions) {
        fetchMembersForGroup({
          groupId: currentGroupId,
          accountIds: [account],
          metaId: `fetch-${id}`,
          appendResult: true,
        });
      }
    }
  }, [account, currentAccount, currentGroupId]); // eslint-disable-line

  const adminSuggestions = useMemo(() => {
    const adminMembers = members.filter(
      (member = {}) => (member as any).roleId === roles.admin
    );
    adminMembers.unshift({
      account: { id: null, firstName: 'Unassign', lastName: 'Number' },
    });
    return adminMembers.map((item = {}) => ({
      label: `${(item as any).account.firstName} ${
        (item as any).account.lastName
      }`,
      value: (item as any).account.id,
      phoneNumber: (item as any).account.phoneNumber,
    }));
  }, [members]);

  const handleToggleCallForwarding = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updatePhoneNumber({
      phoneNumberId: id,
      accountId: account,
      allowCallForwarding: event.target.checked,
    });
    setAllowCallForwarding(event.target.checked);
  };

  const handleToggleTwoWayMessaging = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updatePhoneNumber({
      phoneNumberId: id,
      accountId: account,
      twoWayMessagingEnabled: event.target.checked,
    });
    setTwoWayMessagingEnabled(event.target.checked);
  };

  const onMemberSearchChange = useCallback(
    (event, value, reason) => {
      if (value && value.length > 1 && reason === 'input') {
        fetchMembersForGroup({
          groupId: currentGroupId,
          search: value,
          metaId: `fetch-${id}`,
        });
      } else if (reason === 'clear') {
        fetchMembersForGroup({
          groupId: currentGroupId,
          metaId: `fetch-${id}`,
        });
      }
    },
    [currentGroupId, fetchMembersForGroup, id]
  );

  useEffect(() => {
    if (disableForwarding) {
      setAllowCallForwarding(false);
    }
  }, [disableForwarding]);

  const handleAssignAccount = useCallback(
    (event, option) => {
      if (option) {
        setDisableForwarding(!option.phoneNumber);
        setCurrentAccount(option);
        updatePhoneNumber({
          phoneNumberId: id,
          accountId: option.value,
        });
      }
    },
    [id, updatePhoneNumber]
  );

  const handleDelete = useCallback(() => {
    onDelete(id);
  }, [onDelete, id]);

  return (
    <Grid item xs={12}>
      <Box p={2} border={1} borderRadius={8} borderColor='text.secondary'>
        <Hidden smUp>
          <Divider style={{ marginTop: 10, marginBottom: 10 }} />
        </Hidden>

        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={12} md={3}>
            <Typography style={{ fontWeight: 600 }}>
              {displayedPhoneNumber}
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              onInputChange={onMemberSearchChange}
              onChange={handleAssignAccount}
              options={adminSuggestions}
              label='Assigned To'
              value={currentAccount}
              loading={memberLoading}
            />
          </Grid>

          <Grid
            item
            xs={isCurrentGroupCouncil ? 10 : 9}
            md={isCurrentGroupCouncil ? 2 : 4}
          >
            <Tooltip
              title={
                disableForwarding
                  ? 'Call Forwarding is Disabled: Member is missing phone number'
                  : ''
              }
              placement='top'
              arrow
            >
              <FormControlLabel
                disabled={disableForwarding}
                control={
                  <Switch
                    checked={allowCallForwarding}
                    color='primary'
                    type='checkbox'
                    onChange={handleToggleCallForwarding}
                  />
                }
                label='Call Forwarding'
              />
            </Tooltip>
          </Grid>

          {isCurrentGroupCouncil && (
            <Grid item xs={10} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={twoWayMessagingEnabled}
                    color='primary'
                    type='checkbox'
                    onChange={handleToggleTwoWayMessaging}
                  />
                }
                label='Two-Way Messaging'
              />
            </Grid>
          )}

          <Grid item xs={1}>
            <Tooltip id='deleteButtonTooltip' title='Delete Phone Number'>
              <IconButton
                id='delete'
                onClick={handleDelete}
                loading={deleteLoading}
              >
                <Delete />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

export default memo(PhoneNumberItem);
